/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { FC, useMemo, useState } from 'react';
import { TId } from '../../components/common/generic/tree/types';
import {
  TMap,
  TMethods,
  TMethodsMap,
  TFireEvent,
  TStateManager,
  TMethod,
  TEventsHandlers,
  ImperativeComponentContext,
} from './types';

/**
 * Permite la creación de componentes multi-instancia que ofrecen métodos para
 * trabajar sobre su estado o su comportamiento interno.
 * 
 * @example
 * const [methods, events, Imperative] = makeImperativeComponent<
    {
      age: number;
    },
    { blink: number }
  >()(
    {} as {
      name: string;
    },
    {
      setName(setState, name: string) {
        setState({ name });
      },
    },
    ({ age, name, useEvents }) => {
      const [isBlinking, setIsBlinking] = useState(false);

      useEvents({
        blink() {
          setInterval(() => setIsBlinking((current) => !current), 300);
        },
      });

      return (
        <Box sx={{ background: isBlinking ? 'red' : undefined }}>
          {age} {name}
        </Box>
      );
    },
  );

  methods
 */
export default function makeImperativeComponent<
  ComponentProps extends TMap,
  Events extends TMap = TMap,
>() {
  return function ImperativeComponent<
    State extends TMap,
    Methods extends TMethods<State>,
  >({
    Component,
    initialState,
    methods,
  }: {
    initialState?: State;
    methods?: Methods;
    Component: FC<Omit<ComponentProps, 'id'> & State>;
  }): [
    TMethodsMap<Methods>,
    TFireEvent<Events>,
    FC<ComponentProps & { id: TId }>,
  ] {
    /**
     * State methods
     */

    const setStates: Record<string, TStateManager<State>> = {};
    const actualMethods: TMethodsMap<Methods> = {} as TMethodsMap<Methods>;
    Object.entries(methods ?? {}).forEach(([key, method]) => {
      actualMethods[key as keyof Methods] = ((id, ...args: any) => {
        if (setStates[id]) {
          method(setStates[id], ...args);
          return true;
        } else {
          /* console.warn(
            `No setState found with id ${id}. Maybe the component was not mounted yet`,
          ); */
          return false;
        }
      }) as TMethod<Methods[keyof Methods]>;
    });

    /**
     * Events
     */
    const eventsHandlers: TEventsHandlers<Events> = {};

    const fireEvent: TFireEvent<Events> = <K extends keyof Events>(
      id: TId,
      ev: K,
      args: Events[K],
    ) => {
      if ((eventsHandlers[id]?.[ev] ?? []).length === 0) {
        /* console.warn(
          `No component with id ${id}. Maybe the component was not mounted yet`,
        ); */ return false;
      } else {
        eventsHandlers[id]?.[ev]?.forEach((current) => current(args));
        return true;
      }
    };

    /**
     * Component
     */

    const ActualComponent: FC<ComponentProps & { id: TId }> = ({
      id,
      ...props
    }) => {
      const [state, innerSetState] = useState(initialState as State);

      setStates[id] = (newState) =>
        setTimeout(
          () => innerSetState((current) => ({ ...current, ...newState })),
          0,
        );

      return (
        <ImperativeComponentContext.Provider
          value={useMemo(() => ({ id, eventsStore: eventsHandlers }), [id])}
        >
          <Component {...props} {...state} />
        </ImperativeComponentContext.Provider>
      );
    };

    return [actualMethods, fireEvent, ActualComponent];
  };
}
